import React from "react";
import { motion } from "framer-motion";
import { 
  Check, 
  X, 
  Settings, 
  Calendar, 
  Zap,
  Phone,
  MessageSquare,
  Clock,
  Layers,
  Database,
  Shield,
  Activity,
  UserPlus,
  Users,
  AlertTriangle
} from "lucide-react";
import { Card } from "../ui/card";

const AshaVsDentinaReceptionistSlide = () => {
  const featureComparison = [
    { 
      feature: "Complex Time-Blocked Rules", 
      asha: "Full Support", 
      dentina: "None / Basic", 
      icon: Clock,
      ashaSub: "Operatory & Provider specific blocks",
      dentinaSub: "Simple first-available only"
    },
    { 
      feature: "Book into Correct Operatory", 
      asha: "Intelligent Routing", 
      dentina: "1-Column Only", 
      icon: Layers,
      ashaSub: "Books correctly to avoid manual moves",
      dentinaSub: "Staff must move appts manually"
    },
    { 
      feature: "Customizable Parameters", 
      asha: "100+ Settings", 
      dentina: "< 20 Settings", 
      icon: Settings,
      ashaSub: "Deeply tailored to your workflow",
      dentinaSub: "Generic configuration"
    },
    { 
      feature: "HIPAA Compliance", 
      asha: "Fully Compliant", 
      dentina: "Partially Compliant", 
      icon: Shield,
      ashaSub: "Enterprise-grade security",
      dentinaSub: "Risk of violations"
    },
    { 
      feature: "Appointment Types", 
      asha: "Unlimited Types", 
      dentina: "Limited Types", 
      icon: Calendar,
      ashaSub: "Map every procedure code",
      dentinaSub: "Restricted list"
    },
    { 
      feature: "Proxy Call Handling", 
      asha: "Caregiver Support", 
      dentina: "Not Supported", 
      icon: Users,
      ashaSub: "Family member booking flow",
      dentinaSub: "Single patient focus"
    },
    { 
      feature: "Insurance Collection", 
      asha: "Comprehensive", 
      dentina: "Basic / Minimal", 
      icon: Database,
      ashaSub: "Captures full card & member details",
      dentinaSub: "Misses critical data"
    },
    { 
      feature: "Automated ROI Tracking", 
      asha: "Real-Time Dashboard", 
      dentina: "None", 
      icon: Activity,
      ashaSub: "See exact revenue earned",
      dentinaSub: "No production visibility"
    },
    { 
      feature: "Smart SMS Alerts", 
      asha: "Emergency / New Patient", 
      dentina: "None", 
      icon: MessageSquare,
      ashaSub: "Instant staff notifications",
      dentinaSub: "Standard logging only"
    },
    { 
      feature: "Provider Concurrency", 
      asha: "Managed Rules", 
      dentina: "None", 
      icon: UserPlus,
      ashaSub: "Prevents double-booking limits",
      dentinaSub: "Ignores provider overlaps"
    },
    { 
      feature: "Escalation Rules", 
      asha: "Scenario Based", 
      dentina: "None", 
      icon: AlertTriangle,
      ashaSub: "Custom transfers for emergencies",
      dentinaSub: "Standard transfer only"
    }
  ];

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-8 lg:p-12 font-sans overflow-hidden">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center flex-shrink-0 mb-6"
      >
        <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4 tracking-tight">
          Asha vs Dentina Comparison: <span className="text-blue-600">AI Receptionist</span>
        </h2>
        
        {/* TLDR Callout */}
        <div className="bg-blue-600 rounded-2xl p-4 text-white shadow-md border border-blue-500 max-w-5xl mx-auto">
            <div className="flex items-center gap-4 justify-center">
                <div className="bg-white/20 p-2 rounded-lg flex-shrink-0">
                    <Zap className="w-5 h-5 text-yellow-300" />
                </div>
                <p className="text-lg md:text-xl font-medium leading-tight text-center">
                    <span className="font-bold underline decoration-yellow-400 underline-offset-4">TL;DR:</span> Asha supports <span className="text-yellow-200 font-bold">complex, time-blocked scheduling rules</span>, whereas Dentina is limited to basic one-column booking.
                </p>
            </div>
        </div>
      </motion.div>

      {/* Table */}
      <div className="flex-1 flex flex-col min-h-0">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex-1 overflow-hidden"
        >
           <Card className="h-full border-slate-200 shadow-sm flex flex-col bg-white overflow-hidden">
              {/* Table Header */}
              <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex-shrink-0">
                  <div className="flex items-center">
                      <div className="w-[35%] font-bold text-lg text-slate-700 pl-4">
                          Detailed Feature Comparison
                      </div>
                      <div className="w-[32.5%] text-center font-bold text-blue-600 text-lg">
                          Asha (Amy)
                      </div>
                      <div className="w-[32.5%] text-center font-bold text-slate-400 text-lg">
                          Dentina
                      </div>
                  </div>
              </div>
              
              {/* Table Body */}
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                  {featureComparison.map((item, idx) => (
                      <div 
                          key={idx} 
                          className="flex items-stretch border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors"
                      >
                          {/* Feature Column */}
                          <div className="w-[35%] py-3 pl-6 pr-4 flex items-center">
                              <div className="flex items-center gap-3">
                                  <div className="p-1.5 bg-blue-50 rounded-lg flex-shrink-0">
                                      <item.icon className="w-4 h-4 text-blue-600" />
                                  </div>
                                  <span className="font-semibold text-slate-800 text-sm">
                                      {item.feature}
                                  </span>
                              </div>
                          </div>
                          
                          {/* Asha Column */}
                          <div className="w-[32.5%] py-3 px-4 flex items-center justify-center">
                              <div className="flex flex-col items-center text-center">
                                  <div className="flex items-center gap-2 mb-1">
                                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                                      <span className="text-blue-600 font-bold text-sm">
                                          {item.asha}
                                      </span>
                                  </div>
                                  <span className="text-slate-500 text-xs leading-tight">
                                      {item.ashaSub}
                                  </span>
                              </div>
                          </div>
                          
                          {/* Dentina Column */}
                          <div className="w-[32.5%] py-3 px-4 flex items-center justify-center">
                              <div className="flex flex-col items-center text-center">
                                  <div className="flex items-center gap-2 mb-1">
                                      <X className="w-4 h-4 text-red-300 flex-shrink-0" />
                                      <span className="text-slate-500 font-medium text-sm">
                                          {item.dentina}
                                      </span>
                                  </div>
                                  <span className="text-slate-400 text-xs leading-tight">
                                      {item.dentinaSub}
                                  </span>
                              </div>
                          </div>
                      </div>
                  ))}
              </div>
           </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default AshaVsDentinaReceptionistSlide;
